import { createContext, createMemo, type JSX, useContext } from 'solid-js'
import { authService } from '~/services/auth.service'
import { authStore } from '~/stores/auth'
import { companyStore, setCompanyStore } from '~/stores/company'

type CompanyContextType = {
  currentCompany: () => { id: string | null; name: string | null; slug: string | null; logo: string | null; domain: string | null }
  companies: () => Array<{
    company: { id: string; name: string; slug: string; domain?: string; logo?: string }
    role: string
    isDefault: boolean
  }>
  switchCompany: (companyId: string) => Promise<void>
  hasMultipleCompanies: () => boolean
}

const CompanyContext = createContext<CompanyContextType>()

export function CompanyProvider(props: { children: JSX.Element }) {
  const currentCompany = () => ({
    id: companyStore.id,
    name: companyStore.name,
    slug: companyStore.slug,
    logo: companyStore.logo,
    domain: companyStore.domain
  })

  const companies = () => authStore.companies

  const hasMultipleCompanies = createMemo(() => authStore.companies.length > 1)

  const switchCompany = async (companyId: string) => {
    await authService.selectCompany(companyId)
    const company = authStore.companies.find(c => c.company.id === companyId)?.company
    if (company) {
      setCompanyStore({
        id: company.id,
        name: company.name,
        slug: company.slug,
        logo: company.logo ?? null,
        domain: company.domain ?? null
      })
    }
  }

  const value: CompanyContextType = {
    currentCompany,
    companies,
    switchCompany,
    hasMultipleCompanies
  }

  return <CompanyContext.Provider value={value}>{props.children}</CompanyContext.Provider>
}

export function useCompany() {
  const context = useContext(CompanyContext)
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}
